import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { date, fine, testingWords, weight } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { PrintToolbar } from '../../components/print/PrintDocument';
import { Barcode } from '../../components/print/Barcode';

interface Melt {
  id: string;
  doc_no: string;
  status: string;
  received_date: string | null;
  branch_name: string;
  metal_name: string;
  refiner_name: string | null;
  melter_name: string | null;
  testing_method: string | null;
  assay_cert_no: string | null;
  assayed_by: string | null;
  bars: {
    line_no: number;
    bar_no: string;
    weight: string;
    fine_wt: string;
    assay_pct: string | null;
    hallmark_uid: string | null;
    purity_code: string;
    fineness: string;
    item_status: string | null;
  }[];
}

/**
 * A tag for every bar from a melt — the card that travels with the bar in the
 * safe and is scanned at the counter.
 *
 * Each carries what anyone handling the bar needs without looking it up: the
 * shop, the metal and purity, the assay, the weight and the fine weight, the
 * hallmark number, where it came from and when, and a barcode of the bar
 * number. Two to a row on A4, cut along the border.
 */
export function BarTagsPrintPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, error, isLoading } = useQuery({
    queryKey: ['melting', id],
    queryFn: () => api.get<Melt>(`/meltings/${id}`),
  });
  const { header } = usePrintHeader(data?.branch_name);

  if (isLoading) return <Loading what="Preparing the tags" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return null;

  const melter = data.refiner_name ?? data.melter_name ?? 'In-house';
  const bars = data.bars ?? [];

  return (
    <>
      <PrintToolbar onBack={() => navigate(`/melting?tab=BARS`)} fileName={`${data.doc_no} bar tags`} />
      {bars.length === 0 ? (
        <p className="text-sm text-ink-soft">This melt has no bars yet.</p>
      ) : (
        <div className="print-sheet !max-w-4xl">
          <div className="grid grid-cols-2 gap-4">
            {bars.map((b) => (
              <div key={b.line_no} className="keep-together rounded-md border-2 border-dashed border-ink/40 p-4">
                <div className="flex items-start justify-between gap-3 border-b border-gold-600 pb-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold leading-tight">{header.companyName}</p>
                    <p className="text-[10px] text-ink-soft">{header.branchName}</p>
                  </div>
                  <p className="shrink-0 rounded bg-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    {data.metal_name} bar
                  </p>
                </div>

                <div className="mt-2 flex items-end justify-between gap-3">
                  <div>
                    <p className="doc-label">Bar no.</p>
                    <p className="num text-xl font-bold leading-tight">{b.bar_no}</p>
                  </div>
                  <div className="text-right">
                    <p className="doc-label">Purity</p>
                    <p className="num text-xl font-bold leading-tight">{b.purity_code}</p>
                  </div>
                </div>

                <table className="mt-2 w-full text-[12px]">
                  <tbody>
                    <tr>
                      <td className="py-0.5 text-ink-soft">Weight</td>
                      <td className="num py-0.5 text-right font-semibold">{weight(b.weight)}</td>
                      <td className="py-0.5 pl-4 text-ink-soft">Assay</td>
                      <td className="num py-0.5 text-right font-semibold">
                        {b.assay_pct
                          ? `${Number(b.assay_pct).toFixed(2)}%`
                          : `${(Number(b.fineness) * 100).toFixed(2)}%`}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5 text-ink-soft">Fine wt</td>
                      <td className="num py-0.5 text-right font-semibold">{fine(b.fine_wt)}</td>
                      <td className="py-0.5 pl-4 text-ink-soft">HUID</td>
                      <td className="num py-0.5 text-right font-semibold">{b.hallmark_uid ?? '—'}</td>
                    </tr>
                    <tr>
                      <td className="py-0.5 text-ink-soft">Melt</td>
                      <td className="num py-0.5 text-right">{data.doc_no}</td>
                      <td className="py-0.5 pl-4 text-ink-soft">Cast</td>
                      <td className="num py-0.5 text-right">{data.received_date ? date(data.received_date) : '—'}</td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-0.5 text-ink-soft">Melted by</td>
                      <td className="py-0.5 text-right" colSpan={3}>
                        {melter}
                        {data.testing_method ? ` · tested by ${testingWords(data.testing_method)}` : ''}
                        {data.assay_cert_no ? ` · cert ${data.assay_cert_no}` : ''}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-2 flex flex-col items-center">
                  <Barcode value={b.bar_no} height={38} />
                  <p className="num text-[10px] tracking-widest">{b.bar_no}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
